import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../components/AuthProvider";
import { apiUrl } from "../../lib/api";

const fontOrbitron = { fontFamily: "'Orbitron', 'Arial', sans-serif" };
const fontRajdhani = { fontFamily: "'Rajdhani', 'Arial', sans-serif" };
const fontMono = { fontFamily: "'Share Tech Mono', monospace" };

const BUSINESS_TYPES = ["Agency","Service business","Coach / consultant","Creator / personal brand","Ecommerce","Local business","Other"];
const MOMENTUM_BLOCKERS = ["Not enough leads","Leads not replying","Booked calls not closing","Too much manual follow-up","Delivery is messy","Content is inconsistent","Old leads are sitting idle"];
const PRIMARY_GOALS = ["Generate and organize leads","Follow up and reactivate prospects","Reduce no-shows and improve appointment handling","Build content systems","Improve delivery operations","Show pipeline and conversion visibility"];

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ ...fontMono, fontSize: 9, color: "rgba(45,200,150,0.6)", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
    {children}
  </div>
);

const TextInput = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    value={value || ""}
    onChange={onChange}
    placeholder={placeholder}
    style={{ width: "100%", background: "rgba(45,200,150,0.04)", border: "1px solid rgba(45,200,150,0.2)", borderRadius: 4, padding: "10px 14px", color: "#fff", ...fontMono, fontSize: 13, outline: "none", boxSizing: "border-box", letterSpacing: "0.02em" }}
  />
);

const SelectGrid = ({ options, value, onChange }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    {options.map(opt => (
      <button key={opt} onClick={() => onChange(opt)} style={{ background: value === opt ? "rgba(45,200,150,0.1)" : "rgba(255,255,255,0.02)", border: `1px solid ${value === opt ? "#2DC896" : "rgba(255,255,255,0.08)"}`, borderRadius: 4, padding: "10px 14px", textAlign: "left", ...fontMono, fontSize: 12, color: value === opt ? "#2DC896" : "rgba(255,255,255,0.6)", cursor: "pointer", transition: "all 0.15s" }}>
        {value === opt ? "✓ " : ""}{opt}
      </button>
    ))}
  </div>
);

const UserProfileSettings = () => {
  const { user, session } = useAuth();
  const [manageLoading, setManageLoading] = React.useState(false);
  const [manageError, setManageError] = React.useState("");

  const handleManageSubscription = async () => {
    if (manageLoading) return;
    setManageError("");
    setManageLoading(true);
    try {
      if (!user || !session?.access_token) {
        setManageError("Please log in again.");
        setManageLoading(false);
        return;
      }
      const res = await fetch(apiUrl('/api/stripe/portal-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok || !json.url) {
        setManageError(json.error || 'Could not open portal. Try again.');
        setManageLoading(false);
        return;
      }
      window.location.href = json.url;
    } catch (e) {
      setManageError('Network error. Try again.');
      setManageLoading(false);
    }
  };

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("identity");
  const [profile, setProfile] = useState(null);
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("");
  const [form, setForm] = useState({ business_name: "", main_offer: "", ideal_customer: "", business_type: "", momentum_blocker: "", primary_goal: "" });

  useEffect(() => {
    const load = async () => {
      if (!user) { navigate("/user-login"); return; }
      setEmail(user.email || "");
      const { data: prof } = await supabase.from("profiles").select("*").eq("id", user.id).maybeSingle();
      if (prof) {
        setProfile(prof);
        setPlan(prof.plan || "recruit");
        const ctx = prof.onboarding_context || {};
        setForm({ business_name: ctx.business_name || "", main_offer: ctx.main_offer || "", ideal_customer: ctx.ideal_customer || "", business_type: ctx.business_type || prof.business_type || "", momentum_blocker: ctx.momentum_blocker || "", primary_goal: ctx.primary_goal || "" });
      }
      setLoading(false);
    };
    load();
  }, [navigate, user]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    const uid = user?.id;
    if (!uid) { setError("Session expired."); setSaving(false); return; }
    const ctx = { ...((profile?.onboarding_context) || {}), business_name: form.business_name.trim() || null, main_offer: form.main_offer.trim() || null, ideal_customer: form.ideal_customer.trim() || null, business_type: form.business_type || null, momentum_blocker: form.momentum_blocker || null, primary_goal: form.primary_goal || null, updated_at: new Date().toISOString() };
    const { error: err } = await supabase.from("profiles").update({ onboarding_context: ctx, updated_at: new Date().toISOString() }).eq("id", uid);
    if (err) { setError("Save failed. Try again."); setSaving(false); return; }
    setProfile(prev => ({ ...prev, onboarding_context: ctx }));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    setSaving(false);
  };

  const tabs = [{ id: "identity", label: "IDENTITY" }, { id: "context", label: "BUSINESS CONTEXT" }, { id: "account", label: "ACCOUNT" }];

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#05060f", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ ...fontMono, color: "#2DC896", fontSize: 12, letterSpacing: "0.15em" }}>LOADING OPERATOR PROFILE...</div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#05060f", padding: "clamp(16px,4vw,40px) clamp(12px,3vw,24px)", boxSizing: "border-box" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", ...fontMono, fontSize: 10, color: "rgba(45,200,150,0.5)", letterSpacing: "0.12em", cursor: "pointer", marginBottom: 16, padding: 0 }}>← BACK</button>
          <div style={{ ...fontOrbitron, fontSize: 10, color: "#2DC896", letterSpacing: "0.15em", marginBottom: 6 }}>RECALLCORE OPERATOR</div>
          <h1 style={{ ...fontOrbitron, fontSize: 24, fontWeight: 900, color: "#fff", letterSpacing: "0.06em", margin: 0, textTransform: "uppercase" }}>ACCOUNT SETTINGS</h1>
          <div style={{ ...fontMono, fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 6 }}>{email} · {plan.toUpperCase()} TIER</div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 28, borderBottom: "1px solid rgba(45,200,150,0.1)", paddingBottom: 0 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ background: "none", border: "none", borderBottom: activeTab === t.id ? "2px solid #2DC896" : "2px solid transparent", padding: "8px 16px", ...fontMono, fontSize: 10, color: activeTab === t.id ? "#2DC896" : "rgba(255,255,255,0.35)", letterSpacing: "0.12em", cursor: "pointer", transition: "all 0.15s", marginBottom: -1 }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* IDENTITY TAB */}
        {activeTab === "identity" && (
          <div style={{ background: "#0a0b1a", border: "1px solid rgba(45,200,150,0.12)", borderRadius: 8, padding: "28px 24px" }}>
            <Field label="Business Name">
              <TextInput value={form.business_name} onChange={e => setForm(p => ({ ...p, business_name: e.target.value }))} placeholder="e.g. Apex Growth Agency" />
            </Field>
            <Field label="Main Offer">
              <TextInput value={form.main_offer} onChange={e => setForm(p => ({ ...p, main_offer: e.target.value }))} placeholder="e.g. Done-for-you lead generation for coaches" />
            </Field>
            <Field label="Ideal Customer">
              <TextInput value={form.ideal_customer} onChange={e => setForm(p => ({ ...p, ideal_customer: e.target.value }))} placeholder="e.g. Online coaches doing $10K-$50K/month" />
            </Field>
          </div>
        )}

        {/* BUSINESS CONTEXT TAB */}
        {activeTab === "context" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ background: "#0a0b1a", border: "1px solid rgba(45,200,150,0.12)", borderRadius: 8, padding: "28px 24px" }}>
              <Field label="Business Type">
                <SelectGrid options={BUSINESS_TYPES} value={form.business_type} onChange={v => setForm(p => ({ ...p, business_type: v }))} />
              </Field>
            </div>
            <div style={{ background: "#0a0b1a", border: "1px solid rgba(45,200,150,0.12)", borderRadius: 8, padding: "28px 24px" }}>
              <Field label="Biggest Momentum Blocker">
                <SelectGrid options={MOMENTUM_BLOCKERS} value={form.momentum_blocker} onChange={v => setForm(p => ({ ...p, momentum_blocker: v }))} />
              </Field>
            </div>
            <div style={{ background: "#0a0b1a", border: "1px solid rgba(45,200,150,0.12)", borderRadius: 8, padding: "28px 24px" }}>
              <Field label="Primary Goal">
                <SelectGrid options={PRIMARY_GOALS} value={form.primary_goal} onChange={v => setForm(p => ({ ...p, primary_goal: v }))} />
              </Field>
            </div>
          </div>
        )}

        {/* ACCOUNT TAB */}
        {activeTab === "account" && (
          <div style={{ background: "#0a0b1a", border: "1px solid rgba(45,200,150,0.12)", borderRadius: 8, padding: "28px 24px" }}>
            <Field label="Email Address">
              <div style={{ ...fontMono, fontSize: 13, color: "rgba(255,255,255,0.6)", padding: "10px 14px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4 }}>{email}</div>
            </Field>
            <Field label="Current Plan">
              <div style={{ ...fontOrbitron, fontSize: 13, color: "#2DC896", padding: "10px 14px", background: "rgba(45,200,150,0.04)", border: "1px solid rgba(45,200,150,0.2)", borderRadius: 4 }}>{plan.toUpperCase()}</div>
            </Field>
            <Field label="Missions Used">
              <div style={{ ...fontMono, fontSize: 13, color: "rgba(255,255,255,0.6)", padding: "10px 14px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4 }}>{profile?.missions_used ?? 0} of {profile?.missions_limit ?? 1}</div>
            </Field>
            <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <button onClick={() => navigate("/pricing")} style={{ background: "rgba(239,159,39,0.1)", border: "1px solid rgba(239,159,39,0.4)", borderRadius: 4, padding: "10px 24px", ...fontOrbitron, fontSize: 10, color: "#EF9F27", letterSpacing: "0.12em", cursor: "pointer", fontWeight: 700 }}>UPGRADE PLAN →</button>
              <button
                onClick={handleManageSubscription}
                disabled={manageLoading}
                style={{
                  marginLeft: 12,
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  borderRadius: 4,
                  padding: "10px 24px",
                  ...fontOrbitron,
                  fontSize: 10,
                  color: "rgba(255,255,255,0.7)",
                  letterSpacing: "0.12em",
                  cursor: manageLoading ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  transition: "all 0.2s",
                }}
              >
                {manageLoading ? "LOADING..." : "MANAGE SUBSCRIPTION"}
              </button>
              {manageError && (
                <div style={{ marginTop: 12, ...fontMono, fontSize: 11, color: "#E84040", letterSpacing: "0.05em" }}>
                  {manageError}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Save Bar */}
        {activeTab !== "account" && (
          <div style={{ marginTop: 24, display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={handleSave} disabled={saving} style={{ background: saving ? "rgba(45,200,150,0.04)" : "rgba(45,200,150,0.12)", border: "1px solid rgba(45,200,150,0.4)", borderRadius: 4, padding: "12px 28px", ...fontOrbitron, fontSize: 10, fontWeight: 700, color: "#2DC896", letterSpacing: "0.12em", cursor: saving ? "not-allowed" : "pointer", transition: "all 0.2s" }}>
              {saving ? "SAVING..." : "SAVE CHANGES"}
            </button>
            {saved && <div style={{ ...fontMono, fontSize: 11, color: "#2DC896", letterSpacing: "0.1em" }}>✓ SAVED</div>}
            {error && <div style={{ ...fontMono, fontSize: 11, color: "#E84040", letterSpacing: "0.1em" }}>{error}</div>}
          </div>
        )}

      </div>
    </div>
  );
};

export default UserProfileSettings;
